import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Upload, CheckCircle2, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const ctl = "h-9 px-2 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";

const upiLink = (scheme, upi, r) => `${scheme}?${new URLSearchParams({
  pa: upi.upi_id, pn: upi.name || upi.upi_id, am: Number(r.amount).toFixed(2), cu: "INR", tn: `Fuel ${r.vehicle_number} ${r.date}`,
}).toString()}`;

const PayProof = ({ r, onPaid }) => {
  const [file, setFile] = useState(null);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return toast.error("Attach the payment screenshot");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/attachments", fd, { params: { entity_type: "fuel", entity_id: r.id }, headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/gatepass/fuel/${r.id}/paid`, { attachment_id: data.id, upi_ref: ref });
      toast.success("Fuel marked paid"); onPaid();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 border-t border-border pt-3">
      <div className="text-xs font-semibold">2. After paying, attach the screenshot</div>
      <label data-testid="fuel-pay-proof" className={`${ctl} w-full flex items-center gap-2 cursor-pointer ${file ? "border-primary text-primary" : "text-muted-foreground"}`}>
        <Upload size={14} /><span className="truncate text-xs">{file ? file.name : "Payment screenshot (photo / PDF)"}</span>
        <input data-testid="fuel-pay-proof-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
      </label>
      <input data-testid="fuel-pay-ref" className={`${ctl} w-full num`} placeholder="UPI reference no. (optional)" value={ref} onChange={(e) => setRef(e.target.value)} />
      <button data-testid="fuel-pay-mark" disabled={busy} onClick={submit} className="w-full h-10 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-60 transition-colors">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Mark paid
      </button>
    </div>
  );
};

export const FuelPayDialog = ({ r, upi, onClose, onPaid }) => (
  <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="fuel-pay-dialog" className="max-w-sm max-h-[92vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
      <DialogHeader><DialogTitle>Pay fuel by UPI</DialogTitle></DialogHeader>
      {r && (!upi.upi_id ? <p data-testid="fuel-pay-no-upi" className="text-sm text-destructive">The admin has not set the fuel pump UPI ID yet. Ask the admin to set it above the fuel entry form.</p> : (
        <div className="space-y-3">
          <div className="text-center">
            <div data-testid="fuel-pay-amount" className="num text-2xl font-bold">{inr(r.amount)}</div>
            <div className="text-xs text-muted-foreground">{r.vehicle_number} · {r.litres} L · {r.date}</div>
            <div data-testid="fuel-pay-upi" className="num text-xs mt-1">To <b>{upi.name || upi.upi_id}</b> · {upi.upi_id}</div>
          </div>
          <div className="text-xs font-semibold">1. Pay with PhonePe</div>
          <a data-testid="fuel-pay-phonepe" href={upiLink("phonepe://pay", upi, r)} className="w-full h-10 rounded-md bg-[#5f259f] text-white text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"><Smartphone size={14} /> Open PhonePe</a>
          <a data-testid="fuel-pay-other" href={upiLink("upi://pay", upi, r)} className="block text-center text-xs text-primary underline">Other UPI app</a>
          <div className="flex flex-col items-center gap-1">
            <div className="bg-white p-2 rounded"><QRCodeSVG value={upiLink("upi://pay", upi, r)} size={150} fgColor="#000000" /></div>
            <span className="text-[11px] text-muted-foreground">On a computer? Scan with PhonePe</span>
          </div>
          <PayProof r={r} onPaid={onPaid} />
        </div>
      ))}
    </DialogContent>
  </Dialog>
);

export const FuelUpiSetting = ({ upi, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(upi);
  const save = async () => {
    try { const { data } = await api.put("/gatepass/fuel-upi", v); toast.success("Fuel pump UPI saved"); setEdit(false); onSaved(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!edit) return (
    <div data-testid="fuel-upi-setting" className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">Fuel pump UPI:</span>
      <span data-testid="fuel-upi-current" className="num font-semibold">{upi.upi_id ? `${upi.name ? `${upi.name} · ` : ""}${upi.upi_id}` : "not set"}</span>
      <button data-testid="fuel-upi-edit" onClick={() => { setV(upi); setEdit(true); }} className="text-primary flex items-center gap-1 hover:underline"><Pencil size={12} /> {upi.upi_id ? "Change" : "Set"}</button>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input data-testid="fuel-upi-id" className={`${ctl} num w-48`} placeholder="pumpname@ybl" value={v.upi_id} onChange={(e) => setV({ ...v, upi_id: e.target.value })} />
      <input data-testid="fuel-upi-name" className={`${ctl} w-44`} placeholder="Pump name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
      <button data-testid="fuel-upi-save" onClick={save} className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors">Save</button>
      <button data-testid="fuel-upi-cancel" onClick={() => setEdit(false)} className="h-9 px-3 rounded-md border border-input text-xs hover:bg-muted transition-colors">Cancel</button>
    </div>
  );
};
